import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Cookie, Settings, X, Shield, BarChart3, Zap, Sliders } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { claritySetCookieConsent } from '../../lib/clarity';

interface CookiePreferences {
  essential: boolean;
  analytics: boolean;
  performance: boolean;
  functional: boolean;
}

const CookieConsent: React.FC = () => {
  const { t, translations, language } = useLanguage();
  const [isVisible, setIsVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    essential: true, // Always true - cannot be disabled
    analytics: false,
    performance: false,
    functional: false
  });

  useEffect(() => {
    // Check if user has already made a choice
    const savedConsent = localStorage.getItem('cookie-consent');
    if (!savedConsent) {
      // Delay showing banner for better UX
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 1500);
      return () => clearTimeout(timer);
    } else {
      // Load saved preferences
      try {
        const saved = JSON.parse(savedConsent);
        setPreferences(saved.preferences || preferences);
        applyCookieSettings(saved.preferences || preferences);
      } catch (error) {
        console.error('Error parsing saved cookie consent:', error);
        setIsVisible(true);
      }
    }
  }, []);

  const applyCookieSettings = (prefs: CookiePreferences) => {
    // Configure Google Analytics
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': prefs.analytics ? 'granted' : 'denied',
        'ad_storage': 'denied', // We don't use ads
        'functionality_storage': prefs.functional ? 'granted' : 'denied',
        'personalization_storage': prefs.functional ? 'granted' : 'denied',
        'security_storage': 'granted' // Always granted for security
      });
    }

    // Configure Clarity
    claritySetCookieConsent(prefs.analytics);
  };

  const savePreferences = (prefs: CookiePreferences) => {
    const consentData = {
      preferences: prefs,
      timestamp: new Date().toISOString(),
      version: '1.0'
    };

    localStorage.setItem('cookie-consent', JSON.stringify(consentData));
    applyCookieSettings(prefs);
    setIsVisible(false);
    setShowSettings(false);
  };

  const handleAcceptAll = () => {
    const allAccepted: CookiePreferences = {
      essential: true,
      analytics: true,
      performance: true,
      functional: true
    };
    savePreferences(allAccepted);
  };

  const handleRejectOptional = () => {
    const onlyEssential: CookiePreferences = {
      essential: true,
      analytics: false,
      performance: false,
      functional: false
    };
    savePreferences(onlyEssential);
  };

  const handleSaveSettings = () => {
    savePreferences(preferences);
  };

  const togglePreference = (key: keyof CookiePreferences) => {
    if (key === 'essential') return; // Cannot be disabled
    setPreferences(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const cookieTypes = [
    {
      key: 'essential' as keyof CookiePreferences,
      icon: Shield,
      title: t('cookies.types.essential.title'),
      description: t('cookies.types.essential.description'),
      color: 'from-green-500 to-green-600',
      required: true
    },
    {
      key: 'analytics' as keyof CookiePreferences,
      icon: BarChart3,
      title: t('cookies.types.analytics.title'),
      description: t('cookies.types.analytics.description'),
      color: 'from-blue-500 to-blue-600',
      required: false
    },
    {
      key: 'performance' as keyof CookiePreferences,
      icon: Zap,
      title: t('cookies.types.performance.title'),
      description: t('cookies.types.performance.description'),
      color: 'from-purple-500 to-purple-600',
      required: false
    },
    {
      key: 'functional' as keyof CookiePreferences,
      icon: Sliders,
      title: t('cookies.types.functional.title'),
      description: t('cookies.types.functional.description'),
      color: 'from-orange-500 to-orange-600',
      required: false
    }
  ];

  return (
    <>
      <AnimatePresence>
        {isVisible && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="fixed bottom-0 left-0 right-0 z-[60] p-4"
          >
            <div className="max-w-7xl mx-auto">
              <div className="relative overflow-hidden rounded-2xl backdrop-blur-xl bg-white/10 border border-white/20 shadow-2xl">
                {/* Animated background particles */}
                <div className="absolute inset-0 overflow-hidden">
                  {[...Array(6)].map((_, i) => (
                    <motion.div
                      key={i}
                      animate={{
                        x: [0, 30, 0],
                        y: [0, -20, 0],
                        opacity: [0.1, 0.3, 0.1],
                        scale: [1, 1.2, 1]
                      }}
                      transition={{
                        duration: 4 + i,
                        repeat: Infinity,
                        delay: i * 0.5,
                        ease: "easeInOut"
                      }}
                      className={`absolute w-3 h-3 rounded-full ${
                        i % 3 === 0 ? 'bg-red-500' : i % 3 === 1 ? 'bg-blue-500' : 'bg-purple-500'
                      }`}
                      style={{
                        left: `${10 + i * 15}%`,
                        top: `${20 + i * 10}%`
                      }}
                    />
                  ))}
                </div>

                <div className="relative z-10 p-6 lg:p-8">
                  {!showSettings ? (
                    // Main Cookie Banner
                    <div className="flex flex-col lg:flex-row items-start lg:items-center gap-6">
                      <div className="flex items-start space-x-4 flex-1">
                        <motion.div
                          animate={{ rotate: [0, 10, -10, 0] }}
                          transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                          className="w-12 h-12 bg-gradient-to-r from-red-500/20 to-red-600/20 backdrop-blur-sm rounded-xl flex items-center justify-center border border-red-500/30"
                        >
                          <Cookie className="w-6 h-6 text-red-500" />
                        </motion.div>
                        
                        <div className="flex-1">
                          <h3 className="text-lg lg:text-xl font-bold text-white mb-2">
                            {t('cookies.banner.title')}
                          </h3>
                          <p className="text-sm lg:text-base text-white/80 leading-relaxed">
                            {t('cookies.banner.description')} {' '}
                            <button 
                              onClick={() => setShowSettings(true)}
                              className="underline text-red-400 hover:text-red-300 transition-colors"
                            >
                              {t('cookies.banner.learnMore')}
                            </button>
                          </p>
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto lg:flex-shrink-0">
                        <motion.button
                          onClick={() => setShowSettings(true)}
                          whileHover={{ scale: 1.02, y: -1 }}
                          whileTap={{ scale: 0.98 }}
                          className="flex items-center justify-center space-x-2 px-6 py-3 bg-white/10 backdrop-blur-sm text-white border border-white/20 rounded-lg hover:bg-white/20 transition-all duration-200 font-medium"
                        >
                          <Settings className="w-4 h-4" />
                          <span>{t('cookies.banner.customize')}</span>
                        </motion.button>

                        <motion.button
                          onClick={handleRejectOptional}
                          whileHover={{ scale: 1.02, y: -1 }}
                          whileTap={{ scale: 0.98 }}
                          className="px-6 py-3 bg-gray-600/80 backdrop-blur-sm text-white rounded-lg hover:bg-gray-500/80 transition-all duration-200 font-medium border border-gray-500/30"
                        >
                          {t('cookies.banner.rejectOptional')}
                        </motion.button>

                        <motion.button
                          onClick={handleAcceptAll}
                          whileHover={{ scale: 1.02, y: -1 }}
                          whileTap={{ scale: 0.98 }}
                          className="px-6 py-3 bg-gradient-to-r from-red-500 to-red-600 backdrop-blur-sm text-white rounded-lg hover:from-red-600 hover:to-red-700 transition-all duration-200 font-semibold shadow-lg hover:shadow-xl border border-red-400/30"
                        >
                          {t('cookies.banner.acceptAll')}
                        </motion.button>
                      </div>
                    </div>
                  ) : (
                    // Cookie Settings Panel
                    <div>
                      <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-gradient-to-r from-red-500/20 to-red-600/20 backdrop-blur-sm rounded-xl flex items-center justify-center border border-red-500/30">
                            <Settings className="w-5 h-5 text-red-500" />
                          </div>
                          <h3 className="text-xl font-bold text-white">
                            {t('cookies.settings.title')}
                          </h3>
                        </div>
                        <motion.button
                          onClick={() => setShowSettings(false)}
                          whileHover={{ scale: 1.1, rotate: 90 }}
                          whileTap={{ scale: 0.9 }}
                          className="p-2 bg-white/10 backdrop-blur-sm rounded-lg text-white/70 hover:text-white hover:bg-white/20 transition-all duration-200"
                        >
                          <X className="w-5 h-5" />
                        </motion.button>
                      </div>

                      <p className="text-white/80 mb-8 text-sm lg:text-base">
                        {t('cookies.settings.description')}
                      </p>

                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-8">
                        {cookieTypes.map((type, index) => {
                          const IconComponent = type.icon;
                          const isEnabled = preferences[type.key];
                          
                          return (
                            <motion.div
                              key={type.key}
                              initial={{ opacity: 0, y: 20 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ duration: 0.3, delay: index * 0.1 }}
                              className={`p-4 rounded-xl backdrop-blur-sm border transition-all duration-300 ${
                                isEnabled 
                                  ? 'bg-white/20 border-white/30 shadow-lg' 
                                  : 'bg-white/10 border-white/20'
                              }`}
                            >
                              <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center space-x-3">
                                  <motion.div
                                    whileHover={{ scale: 1.1, rotate: 5 }}
                                    className={`w-8 h-8 rounded-lg flex items-center justify-center bg-gradient-to-r ${type.color} backdrop-blur-sm`}
                                  >
                                    <IconComponent className="w-4 h-4 text-white" />
                                  </motion.div>
                                  <h4 className="font-semibold text-white">{type.title}</h4>
                                  {type.required && (
                                    <span className="px-2 py-1 bg-green-500/20 text-green-400 text-xs font-medium rounded-full border border-green-500/30">
                                      {t('cookies.settings.required')}
                                    </span>
                                  )}
                                </div>
                                
                                {!type.required && (
                                  <motion.button
                                    onClick={() => togglePreference(type.key)}
                                    whileTap={{ scale: 0.95 }}
                                    className={`relative w-12 h-6 rounded-full transition-all duration-300 ${
                                      isEnabled 
                                        ? 'bg-gradient-to-r from-red-500 to-red-600' 
                                        : 'bg-white/20 backdrop-blur-sm'
                                    }`}
                                  >
                                    <motion.div
                                      animate={{ x: isEnabled ? 24 : 2 }}
                                      transition={{ type: "spring", stiffness: 500, damping: 30 }}
                                      className="absolute top-1 w-4 h-4 bg-white rounded-full shadow-lg"
                                    />
                                  </motion.button>
                                )}
                              </div>
                              
                              <p className="text-white/70 text-sm leading-relaxed">
                                {type.description}
                              </p>
                            </motion.div>
                          );
                        })}
                      </div>

                      {/* Settings Actions */}
                      <div className="flex flex-col sm:flex-row gap-3 justify-end">
                        <motion.button
                          onClick={() => setShowSettings(false)}
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          className="px-6 py-3 bg-white/10 backdrop-blur-sm text-white border border-white/20 rounded-lg hover:bg-white/20 transition-all duration-200 font-medium"
                        >
                          {t('cookies.settings.cancel')}
                        </motion.button>
                        
                        <motion.button
                          onClick={handleSaveSettings}
                          whileHover={{ scale: 1.02, y: -1 }}
                          whileTap={{ scale: 0.98 }}
                          className="px-6 py-3 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-lg hover:from-red-600 hover:to-red-700 transition-all duration-200 font-semibold shadow-lg hover:shadow-xl border border-red-400/30"
                        >
                          {t('cookies.settings.save')}
                        </motion.button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Gradient borders */}
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-red-500/20 via-transparent to-blue-500/20 opacity-50" />
                <div className="absolute inset-[1px] rounded-2xl bg-gradient-to-r from-transparent via-white/5 to-transparent" />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating Settings Button */}
      <CookieSettingsButton />
    </>
  );
};

// Cookie Settings Float Button
const CookieSettingsButton: React.FC = () => {
  const { t } = useLanguage();
  const [showButton, setShowButton] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);

  useEffect(() => {
    const savedConsent = localStorage.getItem('cookie-consent');
    if (savedConsent) {
      const timer = setTimeout(() => {
        setShowButton(true);
      }, 3000); // Show after 3 seconds
      return () => clearTimeout(timer);
    }
  }, []);

  const handleReopenSettings = () => {
    localStorage.removeItem('cookie-consent');
    window.location.reload();
  };

  if (!showButton) return null;

  return (
    <motion.div
      initial={{ scale: 0, rotate: -180 }}
      animate={{ scale: 1, rotate: 0 }}
      className="fixed bottom-6 right-6 z-50"
    >
      <div className="relative">
        <motion.button
          onClick={handleReopenSettings}
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
          whileHover={{ scale: 1.1, y: -2 }}
          whileTap={{ scale: 0.9 }}
          animate={{ 
            rotate: [0, 5, -5, 0],
            y: [0, -3, 0]
          }}
          transition={{ 
            duration: 4,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          className="w-14 h-14 bg-gradient-to-r from-gray-600/80 to-gray-700/80 backdrop-blur-xl rounded-full shadow-xl hover:shadow-2xl transition-all duration-300 flex items-center justify-center border border-white/20 hover:border-white/30 group"
        >
          <Cookie className="w-6 h-6 text-white group-hover:text-red-400 transition-colors" />
          
          {/* Pulse effect */}
          <div className="absolute inset-0 rounded-full bg-gradient-to-r from-red-500/20 to-red-600/20 animate-ping opacity-75" />
        </motion.button>

        {/* Tooltip */}
        <AnimatePresence>
          {showTooltip && (
            <motion.div
              initial={{ opacity: 0, x: 10, scale: 0.8 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              exit={{ opacity: 0, x: 10, scale: 0.8 }}
              className="absolute right-16 top-1/2 transform -translate-y-1/2 px-3 py-2 bg-black/80 backdrop-blur-sm text-white text-sm rounded-lg whitespace-nowrap border border-white/20"
            >
              {t('cookies.floatingButton.tooltip')}
              <div className="absolute top-1/2 -right-1 transform -translate-y-1/2 w-2 h-2 bg-black/80 rotate-45 border-r border-b border-white/20" />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

export default CookieConsent;